import { type BrowserPushSubscription } from "@core/shared/services/push-notifications.service";
import { parseError, URLS } from "@core/shared/utils";
import { errorToast, successToast } from "@design-system/components/toast";
import { useAction } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { useCallback, useEffect, useState } from "react";

type PushNotificationConfiguration = {
  configured: boolean;
  publicKey?: string;
  subscribed?: boolean;
  subscriptionsCount: number;
};

function isBrowserPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function toApplicationServerKey(publicKey: string) {
  const padding = "=".repeat((4 - (publicKey.length % 4)) % 4);
  const normalized = (publicKey + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const value = window.atob(normalized);

  return Uint8Array.from(value, (character) => character.charCodeAt(0));
}

function toSubscriptionPayload(subscription: PushSubscription) {
  const payload = subscription.toJSON();

  if (!payload.endpoint || !payload.keys?.p256dh || !payload.keys.auth) {
    throw new Error("Invalid browser push subscription");
  }

  return payload as BrowserPushSubscription;
}

export function useBrowserPushNotifications(
  enabled: boolean,
  onSubscribed?: () => void,
) {
  const submit = useAction(URLS.notifications.pushSubscription);
  const [configuration, setConfiguration] =
    useState<PushNotificationConfiguration>();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] =
    useState<NotificationPermission>("default");
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !isBrowserPushSupported()) {
      setIsSupported(false);
      setIsLoading(false);
      return;
    }

    setIsSupported(true);
    setPermission(Notification.permission);

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      const endpoint = subscription?.endpoint
        ? `?endpoint=${encodeURIComponent(subscription.endpoint)}`
        : "";
      const response = await fetch(
        `${URLS.notifications.pushSubscription}${endpoint}`,
      );
      if (!response.ok) throw new Error("Unable to load browser push status");

      const data = (await response.json()) as PushNotificationConfiguration;

      setConfiguration(data);
      setIsSubscribed(Boolean(subscription && (data.subscribed ?? true)));
    } catch {
      setConfiguration(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      errorToast(trans("notifications.browserPushUnsupported"));
      return;
    }

    if (!configuration?.configured || !configuration.publicKey) {
      errorToast(trans("notifications.browserPushUnavailable"));
      return;
    }

    setIsUpdating(true);

    try {
      const nextPermission = await Notification.requestPermission();
      setPermission(nextPermission);

      if (nextPermission !== "granted") {
        errorToast(trans("notifications.browserPushPermissionDenied"));
        return;
      }

      const registration = await navigator.serviceWorker.register(
        "/push-notifications-sw.js",
        { scope: "/", updateViaCache: "none" },
      );
      await registration.update();
      await navigator.serviceWorker.ready;

      const subscription =
        (await registration.pushManager.getSubscription()) ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: toApplicationServerKey(configuration.publicKey),
        }));

      const response = await submit({
        action: "subscribe",
        subscription: toSubscriptionPayload(subscription),
      });

      if (!response.isOK) throw new Error(parseError(response.error));

      setIsSubscribed(true);
      onSubscribed?.();
      successToast(trans("notifications.browserPushEnabled"));
    } catch (error: unknown) {
      errorToast(
        error instanceof Error
          ? error.message
          : trans("notifications.browserPushFailed"),
      );
    } finally {
      setIsUpdating(false);
    }
  }, [configuration, isSupported, onSubscribed, submit]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;

    setIsUpdating(true);

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();

      if (subscription) {
        const response = await submit({
          action: "unsubscribe",
          endpoint: subscription.endpoint,
        });

        if (!response.isOK) throw new Error(parseError(response.error));

        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      successToast(trans("notifications.browserPushDisabled"));
    } catch (error: unknown) {
      errorToast(
        error instanceof Error
          ? error.message
          : trans("notifications.browserPushFailed"),
      );
    } finally {
      setIsUpdating(false);
    }
  }, [isSupported, submit]);

  return {
    isSupported,
    isSubscribed,
    permission,
    isLoading,
    isUpdating,
    isConfigured: Boolean(configuration?.configured),
    subscribe,
    unsubscribe,
  };
}
